/**
 * Top-level error boundary (CUSTOMER_FEEDBACK_PLAN Task 3.3).
 *
 * A caught render error shows a recoverable screen (not a white page) with
 * "Report this" submitting surfaceId app.error-boundary and an allowlisted
 * payload of errorName / errorMessage / truncated stack — never user content.
 */

import { Component, type ErrorInfo, type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { ReportProblemDialog } from "./feedback/ReportProblemDialog.js";
import { BrandLogo } from "./BrandLogo.js";
import { Button } from "./ui/Button.js";

const MAX_STACK = 2000;

function truncateStack(stack: string | undefined): string | undefined {
  if (!stack) return undefined;
  return stack.length > MAX_STACK ? stack.slice(0, MAX_STACK) : stack;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep console signal for local debugging; never send componentStack to the hub
    // (it can reference user-rendered content paths).
    console.error("ErrorBoundary caught", error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}

function ErrorFallback({ error, onReset }: { error: Error; onReset: () => void }) {
  const [reportOpen, setReportOpen] = useState(false);
  const stack = truncateStack(error.stack);

  return (
    <div className="flex min-h-full flex-col items-center justify-center px-4 py-16">
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center"><BrandLogo className="h-7 w-auto" /></div>
        <h1 className="mt-3 text-2xl font-semibold text-ink">Something broke</h1>
        <p className="mt-2 text-sm text-muted">
          The page hit an unexpected error. You can try again, go home, or report it —
          we will get a ticket reference either way.
        </p>
        <div className="mt-8 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button type="button" onClick={onReset}>
            Try again
          </Button>
          <Button type="button" variant="secondary" onClick={() => setReportOpen(true)}>
            Report this
          </Button>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-ink no-underline hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/20"
            onClick={onReset}
          >
            Go home
          </Link>
        </div>
      </div>

      <ReportProblemDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        initialType="bug"
        surfaceId="app.error-boundary"
        prefillBody="The app showed an error screen while I was using it."
        payload={{
          errorName: error.name || "Error",
          errorMessage: (error.message || "").slice(0, 500),
          ...(stack ? { stack } : {}),
        }}
      />
    </div>
  );
}
